import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import Login from './components/Login/Login';
import MasterEventSelector from './components/MasterEvent/MasterEventSelector';
import MasterEventLayout from './components/Layout/MasterEventLayout';
import MasterEventHub from './components/MasterEvent/MasterEventHub';
import MasterEventAdmin from './components/MasterEvent/MasterEventAdmin';
import ActivityDetail from './components/Activity/ActivityDetail';
import Dashboard from './components/Dashboard/Dashboard';
import BettingPage from './components/Betting/BettingPage';
import Leaderboard from './components/Leaderboard/Leaderboard';
import Admin from './components/Admin/Admin';
import MyBets from './components/MyBets/MyBets';
import EventOverview from './components/Events/EventOverview';
import Feed from './components/Feed/Feed';
import ScoringDashboard from './components/Scoring/ScoringDashboard';
import UserProfile from './components/Profile/UserProfile';
import RootAdmin from './components/RootAdmin/RootAdmin';
import { masterEventService } from './services/masterEvent';
import './App.css';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="app-loading">
        <span className="app-loading-icon">⚽</span>
        <p>Loading...</p>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// Redirect old flat routes to first master event
function LegacyRedirect({ subpath }) {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) return;
    const run = async () => {
      const events = await masterEventService.getForUser(user.id);
      if (events.length > 0) {
        const meId = events[0].id;
        const activities = await masterEventService.getActivities(meId);
        const bettingActivity = activities.find((a) => a.type === 'betting');
        if (bettingActivity && subpath) {
          navigate(`/me/${meId}/activity/${bettingActivity.id}/betting/${subpath}`, { replace: true });
        } else {
          navigate(`/me/${meId}`, { replace: true });
        }
      } else {
        navigate('/', { replace: true });
      }
    };
    run();
  }, [user, navigate, subpath]);

  return null;
}

function AppRoutes() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="app-loading">
        <span className="app-loading-icon">⚽</span>
        <p>Loading...</p>
      </div>
    );
  }

  return (
      <Routes>
        <Route path="/login" element={user ? <Navigate to="/" replace /> : <Login />} />
        <Route path="/auth" element={user ? <Navigate to="/" replace /> : <Login />} />

        {/* Master Event Selector (landing) */}
        <Route path="/" element={<ProtectedRoute><MasterEventSelector /></ProtectedRoute>} />

        {/* Profile and Root Admin (top-level, outside master event) */}
        <Route path="/profile" element={<ProtectedRoute><UserProfile /></ProtectedRoute>} />
        <Route path="/root-admin" element={<ProtectedRoute><RootAdmin /></ProtectedRoute>} />

        {/* Master Event routes */}
        <Route path="/me/:id" element={<ProtectedRoute><MasterEventLayout /></ProtectedRoute>}>
          <Route index element={<MasterEventHub />} />
          <Route path="activities" element={<MasterEventHub />} />
          <Route path="feed" element={<Feed />} />
          <Route path="scores" element={<ScoringDashboard />} />
          <Route path="admin" element={<MasterEventAdmin />} />

          {/* Activity routes */}
          <Route path="activity/:actId" element={<ActivityDetail />}>
            <Route index element={<Navigate to="betting/place" replace />} />
            {/* Betting sub-routes (rendered inside ActivityDetail's BettingProvider) */}
            <Route path="betting" element={<Dashboard />} />
            <Route path="betting/place" element={<BettingPage />} />
            <Route path="betting/my-bets" element={<MyBets />} />
            <Route path="betting/events" element={<EventOverview />} />
            <Route path="betting/leaderboard" element={<Leaderboard />} />
            <Route path="betting/admin" element={<Admin />} />
          </Route>
        </Route>

        {/* Legacy redirects for old flat routes */}
        <Route path="/betting" element={<ProtectedRoute><LegacyRedirect subpath="place" /></ProtectedRoute>} />
        <Route path="/my-bets" element={<ProtectedRoute><LegacyRedirect subpath="my-bets" /></ProtectedRoute>} />
        <Route path="/events" element={<ProtectedRoute><LegacyRedirect subpath="events" /></ProtectedRoute>} />
        <Route path="/leaderboard" element={<ProtectedRoute><LegacyRedirect subpath="leaderboard" /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute><LegacyRedirect subpath="admin" /></ProtectedRoute>} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
